import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { Textarea } from "./textarea";
import { Input } from "./input";
import { Label } from "./label";
import { Badge } from "./badge";
import { Progress } from "./progress";
import { toast } from "sonner";
import { 
  Star, 
  ThumbsUp, 
  CheckCircle2, 
  MessageSquare,
  Send,
  ChevronDown
} from "lucide-react";
import { API } from "../../apiConfig";


const StarRating = ({ rating, onRatingChange, size = "default", interactive = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizes = {
    sm: "w-4 h-4",
    default: "w-5 h-5",
    lg: "w-6 h-6"
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} transition-colors`}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          onClick={() => interactive && onRatingChange?.(star)}
        >
          <Star 
            className={`${sizes[size]} ${
              star <= (hoverRating || rating)
                ? "fill-yellow-400 text-yellow-400"
                : "text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review, onHelpful }) => {
  const [marked, setMarked] = useState(false);

  const handleHelpful = async () => {
    if (marked) return;
    const success = await onHelpful(review.review_id);
    if (success) setMarked(true);
  };

  return (
    <Card className="border-0 shadow-none bg-muted/30">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium">{review.user_name}</span>
              {review.is_verified_purchase && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified Purchase
                </Badge>
              )}
            </div>
            <StarRating rating={review.rating} size="sm" />
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(review.created_at).toLocaleDateString()}
          </span>
        </div>
        
        <h4 className="font-medium mb-2">{review.title}</h4>
        <p className="text-sm text-muted-foreground mb-3">{review.content}</p>
        
        <button
          onClick={handleHelpful}
          disabled={marked}
          className={`flex items-center gap-1.5 text-xs ${
            marked ? "text-primary" : "text-muted-foreground hover:text-foreground"
          } transition-colors`}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
          Helpful ({review.helpful_count + (marked ? 1 : 0)})
        </button>
      </CardContent>
    </Card>
  );
};

const ReviewForm = ({ productId, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    if (!title.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    const success = await onSubmit({ rating, title, content });
    if (success) {
      setRating(0);
      setTitle("");
      setContent("");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="mb-2 block">Your Rating</Label>
        <StarRating 
          rating={rating} 
          onRatingChange={setRating} 
          size="lg" 
          interactive 
        />
      </div>
      
      <div>
        <Label htmlFor="review-title">Review Title</Label>
        <Input
          id="review-title"
          placeholder="Summarize your experience"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label htmlFor="review-content">Your Review</Label>
        <Textarea
          id="review-content"
          placeholder="Share your thoughts about this artwork..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          className="mt-1"
        />
      </div>
      
      <Button type="submit" disabled={submitting} className="rounded-full">
        <Send className="w-4 h-4 mr-2" />
        {submitting ? "Submitting..." : "Submit Review"}
      </Button>
    </form>
  );
};

const ProductReviews = ({ productId }) => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    average_rating: 0,
    rating_breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [showAll, setShowAll] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchReviews();
  }, [productId, sortBy]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(
        `${API}/reviews/product/${productId}?sort_by=${sortBy}`
      );
      setReviews(res.data.reviews || []);
      setSummary({
        total: res.data.total || 0,
        average_rating: res.data.average_rating || 0,
        rating_breakdown: res.data.rating_breakdown || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      });
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        ...reviewData
      }, { headers });
      toast.success("Review submitted successfully!");
      setShowForm(false);
      fetchReviews();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
      return false;
    }
  };

  const handleHelpful = async (reviewId) => {
    if (!token) {
      toast.error("Please sign in to mark reviews as helpful");
      return false;
    }
    try {
      await axios.post(`${API}/reviews/${reviewId}/helpful`, {}, { headers });
      return true;
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You've already marked this as helpful");
      }
      return false;
    }
  };

  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div className="space-y-6" data-testid="product-reviews">
      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Customer Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Average Rating */}
            <div className="text-center md:text-left">
              <div className="text-5xl font-bold mb-2">
                {summary.average_rating.toFixed(1)}
              </div>
              <StarRating rating={Math.round(summary.average_rating)} size="lg" />
              <p className="text-sm text-muted-foreground mt-2">
                Based on {summary.total} {summary.total === 1 ? 'review' : 'reviews'}
              </p>
            </div>

            {/* Rating Breakdown */}
            <div className="flex-1 space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = summary.rating_breakdown[star] || 0;
                const percentage = summary.total > 0 ? (count / summary.total) * 100 : 0;
                
                return (
                  <div key={star} className="flex items-center gap-3">
                    <span className="text-sm w-12">{star} star</span>
                    <Progress value={percentage} className="flex-1 h-2" />
                    <span className="text-sm text-muted-foreground w-8">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Write Review Button */}
          {user && !showForm && (
            <Button
              variant="outline"
              className="mt-6 rounded-full"
              onClick={() => setShowForm(true)}
            >
              Write a Review
            </Button>
          )}

          {!user && (
            <p className="mt-6 text-sm text-muted-foreground">
              <a href="/login" className="text-primary hover:underline">Sign in</a> to write a review
            </p>
          )}
        </CardContent>
      </Card>

      {/* Review Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Write Your Review</CardTitle>
          </CardHeader>
          <CardContent>
            <ReviewForm productId={productId} onSubmit={handleSubmitReview} />
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">{summary.total} Reviews</h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border rounded-lg px-3 py-1.5"
            >
              <option value="newest">Newest</option>
              <option value="highest">Highest Rated</option>
              <option value="lowest">Lowest Rated</option>
              <option value="helpful">Most Helpful</option>
            </select>
          </div>

          <div className="space-y-3">
            {displayedReviews.map((review) => (
              <ReviewCard 
                key={review.review_id} 
                review={review} 
                onHelpful={handleHelpful}
              />
            ))}
          </div>

          {reviews.length > 3 && !showAll && (
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowAll(true)}
            >
              <ChevronDown className="w-4 h-4 mr-2" />
              Show All {reviews.length} Reviews
            </Button>
          )}
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <p className="text-center text-muted-foreground py-8">
          No reviews yet. Be the first to review this artwork!
        </p>
      )}
    </div>
  );
};

export default ProductReviews;
export { StarRating };
